import React, { useMemo, useState } from "react"
import { Localized, LocalizedProps } from "@fluent/react"
import { IPropertyLink } from "shared/Types/appTypes"
import ActionSheetModalContent from "shared/Components/Dialog/Options/ActionSheetModalContent"
import OptionsDialog from "shared/Components/Dialog/Options/OptionsDialog"
import { createStyles, makeStyles, Typography } from "@material-ui/core"
import { Roboto } from "../../shared/theme"
import { faCalendarPlus } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const useStyles = makeStyles(theme =>
    createStyles({
        calendarButton: {
            display: "flex",
            alignItems: "center",
            fontFamily: Roboto,
            fontWeight: 500,
            fontSize: theme.typography.body1.fontSize,
            lineHeight: "normal",
            textDecoration: "underline",
            background: 'unset',
            border: 0,
        },
        calendarButtonIcon: {
            marginRight: theme.spacing(1)
        },
        title: {
            fontWeight: 500
        },
        link: {
            display: "block",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            fontFamily: Roboto,
            fontSize: theme.typography.body1.fontSize,
            letterSpacing: 0,
            textAlign: "center",
            lineHeight: 4,
            textDecoration: "unset",
            color: "rgba(0, 0, 0, 0.87)",
            "&:not(:last-child)": {
                borderBottom: "1px solid #cecece"
            }
        }
    })
)

export interface IProps {
    links: IPropertyLink[]
}

type IActionSheetModalProps = React.ComponentProps<typeof ActionSheetModalContent>
type ItemOptions = IActionSheetModalProps["itemOptions"];

const validrels = ["ICS_MEETING_REQUEST", "ICS_APPOINTMENT"]

const getItemOptions = (links: IPropertyLink[]): ItemOptions => {
    return links.map(link => {
        if (!validrels.includes(link.rel)) {
            return null
        }
        if (link.translationKey) {
            return {
                href: link.href,
                translationKey: link.translationKey,
                target: "blank"
            }
        }
        return null
    })
}

interface IBaseOption {
    label?: string
    translationKey?: string
    translationVars?: LocalizedProps["vars"],
    disabled?: boolean,
    textColorType?: "primary" | "error"
}


interface ILinkOption extends IBaseOption {
    href: string
    target?: string
}

interface IActionContent {
    classes: ReturnType<typeof useStyles>,
    itemOptions: ItemOptions[],
    onClose: () => void
}

const ActionContent = (props: IActionContent) => {
    const { classes, itemOptions, onClose } = props
    return <>
        {
            itemOptions ? itemOptions.map((itemOption, i) => {
                const itemOpt = itemOption as ILinkOption
                return itemOpt ? <a key={`ActionContent-link-${i}`} href={itemOpt.href} target={itemOpt.target}
                                    className={classes.link} onClick={onClose}>
                    {itemOpt.translationKey ?
                        <Localized id={itemOpt.translationKey} vars={itemOpt.translationVars}>
                            {itemOpt.label || itemOpt.translationKey}
                        </Localized> : itemOpt.label}
                </a> : null
            }) : null
        }
    </>
}

const CalendarImportLinks = (props: IProps) => {
    const classes = useStyles()
    const { links } = props

    const itemOptions = useMemo(() => getItemOptions(links) as ItemOptions[], [links])

    const [showActionOptions, setActionOptions] = useState(false)

    const hideActionOptions = () => setActionOptions(false)

    return (
        <>
            <div>
                <button className={classes.calendarButton} onClick={() => setActionOptions(true)}>
                    <FontAwesomeIcon icon={faCalendarPlus} size="lg" className={classes.calendarButtonIcon} />
                    <Localized id="meeting-open-in-calendar">
                        <Typography variant="body1" className={classes.title}>Føj møde til kalenderen</Typography>
                    </Localized>
                </button>
                <OptionsDialog open={showActionOptions} onClose={hideActionOptions}>
                    <ActionContent classes={classes} itemOptions={itemOptions} onClose={hideActionOptions} />
                </OptionsDialog>
            </div>
        </>
    )
}

export default CalendarImportLinks
